import React, { useState, useEffect } from 'react';
import { X, Keyboard, Command, ArrowRight } from 'lucide-react';

interface ShortcutItem {
  keys: string[];
  description: string;
  category: string;
}

const SHORTCUTS: ShortcutItem[] = [
  { keys: ['Alt', 'D'], description: 'Go to Dashboard', category: 'Navigation' },
  { keys: ['Alt', 'I'], description: 'Go to Inventory', category: 'Navigation' },
  { keys: ['Alt', 'O'], description: 'Go to Orders', category: 'Navigation' },
  { keys: ['Alt', 'R'], description: 'Go to Reports', category: 'Navigation' },
  { keys: ['Alt', 'S'], description: 'Go to Settings', category: 'Navigation' },
  { keys: ['Alt', 'W'], description: 'Go to Worker Terminal', category: 'Navigation' },
  { keys: ['Alt', 'A'], description: 'Go to Analytics', category: 'Navigation' },
  { keys: ['Alt', 'B'], description: 'Go to Barcode Generator', category: 'Navigation' },
  { keys: ['Ctrl', '/'], description: 'Show this help', category: 'General' },
  { keys: ['Esc'], description: 'Close modal / Cancel', category: 'General' },
  { keys: ['Ctrl', 'S'], description: 'Save (in forms)', category: 'Actions' },
  { keys: ['Ctrl', 'F'], description: 'Focus search', category: 'Actions' },
  { keys: ['Ctrl', 'N'], description: 'New item (context-aware)', category: 'Actions' },
];

export const KeyboardShortcutsHelp: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleToggle = () => setIsOpen(prev => !prev);
    const handleClose = () => setIsOpen(false);

    window.addEventListener('toggle-shortcuts-help', handleToggle);
    window.addEventListener('close-modal', handleClose);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === '/') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('toggle-shortcuts-help', handleToggle);
      window.removeEventListener('close-modal', handleClose);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const groupedShortcuts = SHORTCUTS.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {} as Record<string, ShortcutItem[]>);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Keyboard className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Keyboard Shortcuts
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Speed up your workflow with these shortcuts
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-6">
            {Object.entries(groupedShortcuts).map(([category, shortcuts]) => (
              <div key={category}>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  {category}
                </h3>
                <div className="space-y-2">
                  {shortcuts.map((shortcut, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl"
                    >
                      <span className="text-gray-700 dark:text-gray-300">
                        {shortcut.description}
                      </span>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, keyIndex) => (
                          <React.Fragment key={keyIndex}>
                            <kbd className="px-2.5 py-1.5 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg shadow-sm">
                              {key === 'Ctrl' && navigator.platform.includes('Mac') ? (
                                <Command className="h-3 w-3 inline" />
                              ) : (
                                key
                              )}
                            </kbd>
                            {keyIndex < shortcut.keys.length - 1 && (
                              <span className="text-gray-400 mx-0.5">+</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Command className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-cyan-800 dark:text-cyan-300 mb-1">
                  Pro Tip
                </h4>
                <p className="text-sm text-cyan-700 dark:text-cyan-400">
                  On macOS, use <kbd className="px-1.5 py-0.5 bg-cyan-100 dark:bg-cyan-800 rounded text-xs">Cmd</kbd> instead of{' '}
                  <kbd className="px-1.5 py-0.5 bg-cyan-100 dark:bg-cyan-800 rounded text-xs">Ctrl</kbd> for most shortcuts.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            Press <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-xs font-medium">Ctrl + /</kbd> anytime to show this help
          </p>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsHelp;
